import React, { useCallback, useMemo, useState } from "react";
import { useRemoteSensors } from "@/hooks/useRemoteSensors";
import {
  Box,
  FormControl,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { MenuList, MenuListItemType, MenuListItemVariant } from "@WahooFitness/redesignr";
import { t } from "@lingui/macro";
import { useBikeGearConfig } from "@/hooks/useBikeGearConfig";

interface BikeGearConfigProps {
  appToken: string;
  remoteSensorId: number;
}

const getCogName = (cog: number) => {
  switch (cog) {
    case 1:
      return t`Smallest cog`;
    case 2:
      return t`Second cog`;
    case 3:
      return t`Third cog`;
    case 4:
      return t`Fourth cog`;
    case 5:
      return t`Fifth cog`;
    case 6:
      return t`Sixth cog`;
    case 7:
      return t`Seventh cog`;
    case 8:
      return t`Eighth cog`;
    case 9:
      return t`Ninth cog`;
    case 10:
      return t`Tenth cog`;
    case 11:
      return t`Eleventh cog`;
    case 12:
      return t`Twelfth cog`;
    case 13:
      return t`Thirteenth cog`;
    default:
      return t`Cog #${cog}`;
  }
};

function gearsEquals(a?: number[], b?: number[]) {
  return (
    Array.isArray(a) &&
    Array.isArray(b) &&
    a.length === b.length &&
    a.every((val, index) => val === b[index])
  );
}

const BikeGearConfig: React.FC<BikeGearConfigProps> = ({
  appToken,
  remoteSensorId,
}: BikeGearConfigProps) => {
  const { remoteSensorStatus } = useRemoteSensors(appToken);
  const {
    gearCfg,
    chainringCount,
    handleBigRingChange,
    handleMiddleRingChange,
    handleLittleRingChange,
    saveRearCassetteGears,
    cassetteOptions,
    isKICKRBike,
  } = useBikeGearConfig(remoteSensorId, appToken);

  const frontGearCfgHeader = useMemo(() => {
    return {
      id: "front_chainring_count",
      content: t`Front chainring`,
      variant: MenuListItemVariant.NoAction,
      hidden: gearCfg?.front === undefined,
      secondaryContent: (
        <Box display="flex" flexDirection="column" rowGap={1} mt={1}>
          <ToggleButtonGroup
            exclusive
            fullWidth
            value={chainringCount}
            sx={{ pointerEvents: "none" }}
          >
            {Array.from({ length: chainringCount }).map((_, index) => (
              <ToggleButton key={`front-chainring-${index + 1}`} value={index + 1}>
                {index + 1}x
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      ),
    };
  }, [chainringCount, gearCfg]);

  const bigRingCfgSelection = useMemo(() => {
    const chainrings = gearCfg?.front?.cog_tooth_counts;
    const chainringIndex = chainrings?.length === 3 ? 2 : 1;
    return {
      id: "front_chainring_big_ring",
      content: (
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
          <Box>{t`Big ring`}</Box>
          <FormControl component={Paper} elevation={4} variant="outlined">
            <Select
              id="front_chainring_big_ring_select"
              value={chainrings?.[chainringIndex]}
              renderValue={(value) => `${value}T`}
              disabled={gearCfg?.front === undefined}
              onChange={handleBigRingChange}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 200,
                    marginTop: 10,
                    overflowY: "auto",
                  },
                },
              }}
            >
              {Array.from(
                { length: 70 - Math.max((chainrings?.[chainringIndex - 1] ?? 0) + 1, 0) + 1 },
                (_, i) => 70 - i
              ).map((size) => (
                <MenuItem key={`chainring-option-${size}`} value={size}>
                  {size}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      ),
      variant: MenuListItemVariant.NoAction,
      hidden: gearCfg?.front === undefined || chainringCount < 2,
    };
  }, [chainringCount, gearCfg?.front, handleBigRingChange]);

  const middleRingCfgSelection = useMemo(() => {
    const chainrings = gearCfg?.front?.cog_tooth_counts;
    return {
      id: "front_chainring_middle_ring",
      content: (
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
          <Box>{t`Middle ring`}</Box>
          <FormControl component={Paper} elevation={4} variant="outlined">
            <Select
              id="front_chainring_middle_ring_select"
              value={chainrings?.[1]}
              renderValue={(value) => `${value}T`}
              disabled={gearCfg?.front === undefined}
              onChange={handleMiddleRingChange}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 200,
                    marginTop: 10,
                    overflowY: "auto",
                  },
                },
              }}
            >
              {Array.from(
                {
                  length: Math.max(
                    0,
                    (chainrings?.[chainringCount === 3 ? 2 : 1] ?? 0) - (chainrings?.[0] ?? 0) - 1
                  ),
                },
                (_, i) => i + (chainrings?.[0] ?? 0) + 1
              ).map((size) => (
                <MenuItem key={`chainring-option-${size}`} value={size}>
                  {size}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      ),
      variant: MenuListItemVariant.NoAction,
      hidden: gearCfg?.front === undefined || chainringCount < 3,
    };
  }, [chainringCount, gearCfg, handleMiddleRingChange]);

  const littleRingCfgSelection = useMemo(() => {
    const chainrings = gearCfg?.front?.cog_tooth_counts;
    const indexInChainrings = 0;

    return {
      id: "front_chainring_little_ring",
      content: (
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
          <Box>{chainringCount === 1 ? t`Number of teeth` : t`Little ring`}</Box>
          <FormControl component={Paper} elevation={4} variant="outlined">
            <Select
              id="front_chainring_little_ring_select"
              value={chainrings?.[indexInChainrings]}
              renderValue={(value) => `${value}T`}
              disabled={gearCfg?.front === undefined}
              onChange={handleLittleRingChange}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 200,
                    marginTop: 10,
                    overflowY: "auto",
                  },
                },
              }}
            >
              {Array.from(
                {
                  length: Math.max(
                    0,
                    (chainringCount > 1 ? chainrings?.[indexInChainrings + 1] ?? 0 : 71) - 9
                  ),
                },
                (_, i) => i + 9
              ).map((size) => (
                <MenuItem key={`chainring-option-${size}`} value={size}>
                  {size}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      ),
      variant: MenuListItemVariant.NoAction,
      hidden: gearCfg?.front === undefined,
    };
  }, [chainringCount, gearCfg?.front, handleLittleRingChange]);

  const rearGearCfgHeader = useMemo(() => {
    const cassetteCogCount = gearCfg?.rear?.cog_tooth_counts?.length || 0;
    const cassetteCountOptions = [9, 10, 11, 12, 13];

    return {
      id: "rear_cassette_count",
      content: t`Cassette count`,
      variant: MenuListItemVariant.NoAction,
      hidden: gearCfg?.rear === undefined,
      secondaryContent: (
        <Box display="flex" flexDirection="column" rowGap={1} mt={1}>
          <ToggleButtonGroup
            exclusive
            fullWidth
            value={cassetteCogCount}
            sx={{ pointerEvents: "none" }}
          >
            {cassetteCountOptions.map((count) => (
              <ToggleButton key={`rear-cassette-${count}`} value={count}>
                {count}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      ),
    };
  }, [gearCfg]);

  const [gearSetSelection, setGearSetSelection] = useState<string | undefined>();
  const [customCassette, setCustomCassette] = useState<number[]>();

  const availableGearSets = useMemo(() => {
    const options =
      cassetteOptions?.map((option) => ({ ...option, key: option.description })) || [];
    options.push({
      key: "custom",
      description: t`Custom`,
      gears: [],
    });
    const selectedOption = options.find((option) => {
      return gearsEquals(option.gears, gearCfg?.rear?.cog_tooth_counts);
    });
    if (selectedOption) {
      setGearSetSelection(selectedOption.key);
    } else {
      setGearSetSelection("custom");
      setCustomCassette(
        gearCfg?.rear?.cog_tooth_counts ? [...gearCfg.rear.cog_tooth_counts] : undefined
      );
    }
    return options;
  }, [cassetteOptions, gearCfg?.rear?.cog_tooth_counts]);

  const handleRearCassetteChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      if (!cassetteOptions) {
        return;
      }
      if (event.target.value === "custom") {
        setCustomCassette([...(gearCfg?.rear?.cog_tooth_counts || [])]);
        setGearSetSelection("custom");
        return;
      } else {
        setCustomCassette(undefined);
        const gearSet = cassetteOptions.find((option) => option.description === event.target.value);
        if (gearSet) {
          setGearSetSelection(gearSet.description);
          saveRearCassetteGears(gearSet.gears);
        }
      }
    },
    [cassetteOptions, gearCfg?.rear?.cog_tooth_counts, saveRearCassetteGears]
  );

  const rearCassetteRangeSelection = useMemo((): MenuListItemType => {
    return {
      id: "rear_cassette_range",
      content: (
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
          <Box>{t`Range`}</Box>
          <FormControl component={Paper} elevation={4} variant="outlined">
            <Select
              id="rear_cassette_select"
              value={gearSetSelection}
              defaultValue={availableGearSets?.[0]?.key}
              disabled={gearCfg?.rear === undefined}
              onChange={handleRearCassetteChange}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 200,
                    marginTop: 10,
                    overflowY: "auto",
                  },
                },
              }}
            >
              {availableGearSets?.map((option) => (
                <MenuItem key={option.key} value={option.key}>
                  {option.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      ),
      variant: MenuListItemVariant.NoAction,
      hidden: gearCfg?.rear === undefined,
    };
  }, [availableGearSets, gearCfg?.rear, gearSetSelection, handleRearCassetteChange]);

  const updateCustomCassette = useCallback(
    (event: SelectChangeEvent<{ index: number; teeth: number }>) => {
      if (customCassette === undefined) {
        return;
      }
      const newValue = event.target.value as { index: number; teeth: number };
      const newCustomCassette = [...customCassette].reverse();
      newCustomCassette[newValue.index] = newValue.teeth;
      newCustomCassette.reverse();
      setCustomCassette(newCustomCassette);
      saveRearCassetteGears(newCustomCassette);
    },
    [customCassette, saveRearCassetteGears]
  );

  const customGears = useMemo((): MenuListItemType[] => {
    if (gearSetSelection !== "custom" || customCassette === undefined) {
      return [];
    }
    const toothCounts = [...customCassette].reverse(); // Makes life easier
    const maxMinTeeth = toothCounts.map((_toothcount, index) => {
      const minTeeth = index === 0 ? 9 : toothCounts[index - 1] + 1;
      const maxTeeth = index === toothCounts.length - 1 ? 70 : toothCounts[index + 1] - 1;
      return Array(maxTeeth + 1 - minTeeth)
        .fill(0)
        .map((_, i) => i + minTeeth);
    });

    return toothCounts.map((toothCount, index) => {
      const toothOptions = maxMinTeeth[index].map((teeth) => ({
        teeth,
        index,
      }));
      const currentValue =
        toothOptions.find((option) => option.teeth === toothCount) ?? toothOptions[0];
      return {
        id: `custom_cog_${toothCount}`,
        content: (
          <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
            <Box>{getCogName(index + 1)}</Box>
            <FormControl component={Paper} elevation={4} variant="outlined">
              <Select
                id={`custom_cog_${index}_select`}
                value={currentValue}
                renderValue={(value) => `${value.teeth}T`}
                onChange={updateCustomCassette}
                defaultValue={currentValue}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 200,
                      marginTop: 10,
                      overflowY: "auto",
                    },
                  },
                }}
              >
                {toothOptions.map((option, optionIndex) => (
                  <MenuItem key={`gear-option-${index}-${optionIndex}`} value={option as any}>
                    {option.teeth}T
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        ),
      };
    });
  }, [customCassette, gearSetSelection, updateCustomCassette]);

  if (!remoteSensorStatus) {
    return null;
  }

  if (!gearCfg) {
    return null;
  }

  if (isKICKRBike()) {
    return null;
  }

  return (
    <Box gap={4} display="flex" flexDirection="column">
      <MenuList
        listItems={[
          frontGearCfgHeader,
          bigRingCfgSelection,
          middleRingCfgSelection,
          littleRingCfgSelection,
        ]}
        disableGutters
      />
      <MenuList
        listItems={[rearGearCfgHeader, rearCassetteRangeSelection, ...customGears]}
        disableGutters
      />
    </Box>
  );
};

export { BikeGearConfig };
